import { useMemo, useContext } from 'react'
import { Stack } from '@n26/nxd'
import type { ReactNode, AllHTMLAttributes } from 'react'
import { Link as RadixLink } from '@radix-ui/react-navigation-menu'
import { Text } from '@components/Text'
import { useGetCountryLanguage } from '@hooks/useGetCountryLanguage'
import {
  getCountrySwitcherProps,
  getLanguageSwitcherProps,
} from '@helpers/getLocaleSwitcherProps'
import { usePageVariations } from '@hooks/usePageVariations'
import type {
  LanguageCodes,
  CountryCodes,
  CountryNames,
  FlagIcon,
} from '@constants/countryDetails'
import { Link } from '@components/Link'
import { useLocaliseString } from '@hooks/useLocaliseString'
import { trackGTM } from '@helpers/core/trackGTM'
import { ConfigContext } from '@contexts/ConfigContext'

type HTMLButtonLinkProps = AllHTMLAttributes<HTMLAnchorElement>

type LocaleSwitcherButtonLinkProps = {
  url?: string
  label?: string
  isDisabled?: HTMLButtonLinkProps['disabled']
  children?: ReactNode
  dataTestid?: string
  /** Identifies the element (or elements) that describes the button. */
  ariaDescribedby?: HTMLButtonLinkProps['aria-describedby']
  isPaddingXNarrow?: boolean
  onClick?: () => void
}

const LocaleSwitcherButtonLink = (props: LocaleSwitcherButtonLinkProps) => {
  const {
    url,
    label,
    isDisabled,
    children,
    dataTestid,
    ariaDescribedby,
    isPaddingXNarrow,
    ...rest
  } = props

  const styles = `hover:text-teal ${
    isDisabled
      ? 'text-teal opacity-50 cursor-default'
      : 'hover:bg-teal-alternate text-neutral opacity-100'
  } ${
    isPaddingXNarrow ? 'px-8' : 'px-12'
  } py-12 rounded-md font-medium text-14 w-fit transform background-color`

  const computedUrl = url?.includes('__root') ? url.split('_')[0] : url ?? '/'

  return (
    <RadixLink asChild>
      {isDisabled ? (
        <div
          className={styles}
          data-testid={dataTestid}
          aria-describedby={ariaDescribedby}
        >
          {children || label}
        </div>
      ) : (
        <Link
          url={computedUrl}
          className={styles}
          data-testid={dataTestid}
          aria-describedby={ariaDescribedby}
          {...rest}
        >
          {children || label}
        </Link>
      )}
    </RadixLink>
  )
}

type FlagButtonProps = {
  label: CountryNames
  url: string
  altUrl: string
  countryCode: CountryCodes
  FlagIcon: FlagIcon
}

const FlagButton = ({
  label,
  countryCode,
  url,
  altUrl,
  FlagIcon,
}: FlagButtonProps) => {
  const { country, language } = useGetCountryLanguage()

  return (
    <LocaleSwitcherButtonLink
      isDisabled={countryCode === country}
      isPaddingXNarrow
      url={language === 'en' ? altUrl : url}
    >
      <div className='flex text-left'>
        <FlagIcon className='mr-8 w-16' />
        <Text variant='x-small-bold'>{label}</Text>
      </div>
    </LocaleSwitcherButtonLink>
  )
}

export type CountryDetails = {
  countryCode: CountryCodes
  name: string
  url: string
  altUrl: string
  FlagIcon: FlagIcon
}

type CountrySwitcherDropdownProps = {
  countries: CountryDetails[]
}

const CountrySwitcherDropdown = ({
  countries,
}: CountrySwitcherDropdownProps) => {
  const localiseString = useLocaliseString()

  const config = useContext(ConfigContext)

  // We're using config as a basic feature flag to show/hide Belgium
  // until it's ready to go live
  const countriesFiltered = countries.filter(({ countryCode }) =>
    config?.showBelgium ? true : countryCode !== 'be'
  )

  return (
    <Stack space='24'>
      <Stack space='4' align='flex-start'>
        <Text variant='small-bold'>{localiseString('countryPickerLabel')}</Text>
        <div className='text-neutral-alternate text-left'>
          {localiseString('countryPickerDescription')}
        </div>
      </Stack>
      <div className='grid grid-cols-2 gap-4'>
        {countriesFiltered
          .sort((a, b) =>
            localiseString(a.name).localeCompare(localiseString(b.name))
          )
          .map(({ name, countryCode, url, altUrl, FlagIcon }) => (
            <FlagButton
              label={localiseString(name) as CountryNames}
              key={name}
              countryCode={countryCode}
              url={url}
              altUrl={altUrl}
              FlagIcon={FlagIcon}
            />
          ))}
      </div>
    </Stack>
  )
}

export type UrlDataItem = {
  url: string
  contentfulLanguageLabel: string
  languageCode: LanguageCodes
}

type LanguageSwitcherDropdownProps = {
  urls: UrlDataItem[]
}

const LanguageSwitcherDropdown = ({ urls }: LanguageSwitcherDropdownProps) => {
  const { language } = useGetCountryLanguage()
  const localiseString = useLocaliseString()

  return (
    <Stack space='12' align='flex-start'>
      <Text variant='standard-bold'>
        {localiseString('languagePickerLabel')}
      </Text>
      <Stack space='12' align='center' direction='horizontal'>
        {urls.map(({ url, contentfulLanguageLabel, languageCode }) => (
          <LocaleSwitcherButtonLink
            key={url}
            isDisabled={language === languageCode}
            url={url}
            label={localiseString(contentfulLanguageLabel)}
            onClick={() => {
              trackGTM({ event: 'language_switcher', value: url })
            }}
          />
        ))}
      </Stack>
    </Stack>
  )
}

export const LocaleSwitcher = ({
  excludeCountrySwitcher,
}: {
  excludeCountrySwitcher?: boolean
}) => {
  const { localizedUrls } = usePageVariations()
  const { country } = useGetCountryLanguage()
  const languageSwitcherProps = useMemo(
    () => getLanguageSwitcherProps(localizedUrls, country),
    [localizedUrls, country]
  )
  const countrySwitcherProps = useMemo(
    () => getCountrySwitcherProps(localizedUrls),
    [localizedUrls]
  )

  return (
    <div
      data-testid='locale-switcher'
      className='p-4 max-w-none w-full sm:max-w-xs grid gap-y-12 bg-neutral'
    >
      <LanguageSwitcherDropdown urls={languageSwitcherProps} />
      {!excludeCountrySwitcher && (
        <CountrySwitcherDropdown countries={countrySwitcherProps} />
      )}
    </div>
  )
}
